import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { formatPhone, isValidPhone, normalizePhone } from '@/lib/phone';
import { isBlank } from '@/lib/validators';

export interface ContactDraft {
  // id/isMain существующего контакта организации сохраняем при редактировании и echo-им серверу
  // (точное сопоставление набора по id; round-trip признака «основной»). Для нового контакта — undefined.
  readonly id?: number;
  readonly contactName: string;
  readonly phoneNumber: string;
  readonly isMain?: boolean;
}

interface ContactDialogProps {
  readonly opened: boolean;
  /** null — добавление нового контакта; объект — редактирование. */
  readonly initial: ContactDraft | null;
  readonly onSave: (contact: ContactDraft) => void;
  readonly onClose: () => void;
}

// Модал добавления/редактирования контактного лица (имя + телефон).
export default function ContactDialog({ opened, initial, onSave, onClose }: ContactDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});

  useEffect(() => {
    if (opened) {
      setName(initial?.contactName ?? '');
      setPhone(initial?.phoneNumber ? formatPhone(initial.phoneNumber) : '');
      setErrors({});
    }
  }, [opened, initial]);

  function handleSave(): void {
    const next: { name?: string; phone?: string } = {};
    if (isBlank(name)) {
      next.name = t('auth.validation.required');
    }
    if (isBlank(phone)) {
      next.phone = t('auth.validation.required');
    } else if (!isValidPhone(phone)) {
      next.phone = t('auth.validation.phone');
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }
    // Сохраняем id/isMain редактируемого контакта (при добавлении initial=null → undefined).
    onSave({
      id: initial?.id,
      contactName: name.trim(),
      phoneNumber: normalizePhone(phone),
      isMain: initial?.isMain,
    });
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t(initial ? 'app.company.card.contacts.edit' : 'app.company.card.contacts.add')}
    >
      <Stack gap="md">
        <TextInput
          label={t('app.company.card.contacts.name')}
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          error={errors.name}
          withAsterisk
          data-autofocus
        />
        <TextInput
          label={t('auth.fields.phone')}
          value={phone}
          onChange={(event) => setPhone(formatPhone(event.currentTarget.value))}
          error={errors.phone}
          withAsterisk
          inputMode="tel"
          placeholder="+7 (___) ___-__-__"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave}>{t('app.profile.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
